// In GameDataProvider.tsx

import React, { useState, useEffect, ReactNode } from "react";
import axios from "axios";
import { logError } from "../utils/describeError";
import GameDataContext, {
  GameDataContextType,
  GameItem,
  CharacterType,
} from "./GameDataContext";
import { getAccessToken } from "../Components/Utils/TokenRefresh";
import { characters as staticCharacterData } from "../Models/CharacterData";
import { useAuth } from "../Context/AuthContext";
import { CatalogCategory } from "../Models/BuildGameTypes";

export const GameDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [coinCount, setCoinCount] = useState<number | null>(null);
  // False until the first coins/stars/moonrocks fetch has settled, so the HUD
  // can show a placeholder instead of zeros while the app boots.
  const [dataReady, setDataReady] = useState(false);
  const [moonrockCount, setMoonrockCount] = useState<number | null>(null);
  const [starsCount, setStarsCount] = useState<number>(0);
  const [showGrid, setShowGrid] = useState<boolean>(false);
  const [unlockedCharacters, setUnlockedCharacters] = useState<CharacterType[]>(
    []
  );

  const [unlockedItems, setUnlockedItems] = useState<number[][]>([]);
  const [itemHierarchies, setItemHierarchies] = useState<[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [pendingNotifications, setPendingNotifications] = useState<string[]>(
    []
  );

  const [buildCategories, setBuildCategories] = useState<CatalogCategory[]>([]);

  const [starCountdown, setStarCountdown] = useState<number>(0);
  const [starMeta, setStarMeta] = useState<{
    regenSeconds: number;
    cap: number;
  }>({
    regenSeconds: 240,
    cap: 100,
  });
  const [lastStarTimestamp, setLastStarTimestamp] = useState<number>(
    Date.now()
  );

  const starsRef = React.useRef<number>(0);
  const lastTsRef = React.useRef<number>(Date.now());
  const metaRef = React.useRef<{ regenSeconds: number; cap: number }>({
    regenSeconds: 240,
    cap: 100,
  });

  useEffect(() => {
    starsRef.current = starsCount;
  }, [starsCount]);

  useEffect(() => {
    lastTsRef.current = lastStarTimestamp;
  }, [lastStarTimestamp]);

  useEffect(() => {
    metaRef.current = starMeta;
  }, [starMeta]);

  const { accessToken } = useAuth();

  const fetchCharacterData = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return; // signed out (login page): nothing to fetch
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/game/characters`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const characters = response.data.map((char: any) => ({
        characterId: String(char.characterId),
        collectedCount: char.collectedCount || 0,
        level: char.level || 1,
        unlocked: true,
        position: char.position || { x: 0, y: 0, tileX: 0, tileY: 0 },
      }));

      const mergedCharacters = characters.map((char: any) => {
        const characterInfo = staticCharacterData.find(
          (staticChar) => staticChar.name === char.characterId
        );

        return {
          ...characterInfo,
          ...char,
          name: characterInfo?.name || char.characterId,
          spriteSheetUrl: characterInfo?.spriteSheetUrl || "",
          spriteData: characterInfo?.spriteData || {},
          frameRate: characterInfo?.frameRate || 0,
          scale: characterInfo?.scale || 1,
          coinsMultiplier: characterInfo?.coinsMultiplier || 1,
          world: characterInfo?.world || "",
          probability: characterInfo?.probability || 0,
        };
      });

      setUnlockedCharacters(mergedCharacters);
    } catch (error) {
      logError("GameDataProvider: Failed to fetch character data", error);
    }
  };

  const unlockCharacter = async (characterId: string) => {
    try {
      const token = await getAccessToken();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/game/characters/unlock`,
        {
          characterId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Fetch the latest character data after unlocking/updating
      await fetchCharacterData();
    } catch (error) {
      logError("GameDataProvider: Failed to unlock/update character", error);
    }
  };

  const fetchItemHeirarchies = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/itemHierarchies`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setItemHierarchies(response.data.itemHierarchies);
    } catch (err) {
      logError("GameDataProvider: Error fetching item hierarchies", err);
    }
  };

  const fetchBuildCatalog = async () => {
    try {
      const token = await getAccessToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/build-catalog`,
        {
          headers: { Authorization: `Bearer ${token}` }, // remove if your endpoint is public
        }
      );
      setBuildCategories(res.data.categories || []);
    } catch (err) {
      logError("GameDataProvider: Failed to fetch build catalog", err);
      setBuildCategories([]);
    }
  };

  function computeCountdown(
    lastTsMs: number,
    nowMs: number,
    regenSeconds: number,
    stars: number,
    cap: number
  ) {
    if (stars >= cap) return 0; // capped: no countdown
    const elapsed = Math.floor((nowMs - lastTsMs) / 1000);
    const mod = elapsed % regenSeconds;
    const remaining = regenSeconds - mod;
    return remaining;
  }

  useEffect(() => {
    if (!accessToken) return;

    let tickId: number | undefined;
    let pollId: number | undefined;
    let isMounted = true;

    const controller = new AbortController();

    const pollStars = async () => {
      try {
        const token = await getAccessToken();
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/stars`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
        );

        if (!isMounted) return;

        const starCount = Number(res.data?.starCount ?? 0);
        const lastTs = res.data?.lastStarTimestamp
          ? new Date(res.data.lastStarTimestamp).getTime()
          : Date.now();

        const regenSeconds = Number(res.data?.regenSeconds ?? 240);
        const cap = Number(res.data?.cap ?? 100);

        setStarsCount(starCount);
        setLastStarTimestamp(lastTs);
        setStarMeta({ regenSeconds, cap });

        // keep refs in sync for the ticker
        starsRef.current = starCount;
        lastTsRef.current = lastTs;
        metaRef.current = { regenSeconds, cap };

        // immediately compute countdown from server truth
        setStarCountdown(
          computeCountdown(lastTs, Date.now(), regenSeconds, starCount, cap)
        );
      } catch (err) {
        if (!axios.isCancel(err)) {
          logError("GameDataProvider: Error polling /api/stars", err);
        }
      }
    };

    // initial
    pollStars();

    // poll occasionally to stay honest to server (clock drift, tab sleep, etc)
    pollId = window.setInterval(pollStars, 30_000); // every 30s is plenty

    // local ticker every second for smooth UI
    tickId = window.setInterval(() => {
      const now = Date.now();
      const { regenSeconds, cap } = metaRef.current;

      let stars = starsRef.current;
      let lastTs = lastTsRef.current;

      // ✅ locally apply passive regen so UI doesn't "wait for poll"
      if (stars < cap) {
        const elapsedSeconds = Math.floor((now - lastTs) / 1000);
        const starsToAdd = Math.floor(elapsedSeconds / regenSeconds);

        if (starsToAdd > 0) {
          const newStars = Math.min(cap, stars + starsToAdd);
          const actuallyAdded = newStars - stars;

          stars = newStars;
          lastTs = lastTs + actuallyAdded * regenSeconds * 1000;

          // update refs first (so countdown uses the new truth immediately)
          starsRef.current = stars;
          lastTsRef.current = lastTs;

          // update state (only when it changes)
          setStarsCount(stars);
          setLastStarTimestamp(lastTs);
        }
      }

      const next = computeCountdown(lastTs, now, regenSeconds, stars, cap);
      setStarCountdown(next);
    }, 1000);

    return () => {
      isMounted = false;
      controller.abort();
      if (pollId) window.clearInterval(pollId);
      if (tickId) window.clearInterval(tickId);
    };
    // IMPORTANT: include the stuff used by ticker
  }, [accessToken]);

  const fetchNotifications = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/notifs`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPendingNotifications(response.data.userNotifs);
    } catch (error) {
      logError("GameDataProvider: Error fetching notifications", error);
    }
  };

  const updateNotifications = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/notifs`,
        { pendingNotifications: pendingNotifications },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPendingNotifications(response.data.userNotifs);
    } catch (error) {
      logError("GameDataProvider: Error fetching notifications", error);
    }
  };

  const fetchUnlockedItems = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/game/inventory`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.userItems) {
        let returnArr: number[][] = [];

        // Iterate over each category in the response data
        Object.entries(response.data.userItems).forEach(([category, items]) => {
          const Numitems = items as number[];
          // Find the index of the category in itemHierarchies
          const categoryIndex = Object.keys(itemHierarchies).indexOf(category);

          // Check if category exists in itemHierarchies and add items to the correct index
          if (categoryIndex !== -1) {
            // If items exist for the category, map them to an array
            returnArr[categoryIndex] = Numitems; // Directly assign items to the category's index
          } else {
            returnArr.push([]); // If category doesn't exist, add an empty array
          }
        });

        // Set the unlocked items in state
        setUnlockedItems(returnArr);
      }
    } catch (error) {
      logError("GameDataProvider: Failed to get unlocked items", error);
    }
  };

  const calculateNewPrice = (basePrice: number, quantity: number): number => {
    const growthFactor = 2; // Adjust the growth factor as needed
    const newPrice = basePrice * Math.pow(growthFactor, quantity);
    return Math.round(newPrice); // Round to the nearest whole number
  };

  const addCoins = (amount: number) => {
    setCoinCount((prevCount) =>
      prevCount !== null ? prevCount + amount : amount
    );
  };
  const addMoonrock = (amount: number) => {
    setMoonrockCount((prevCount) =>
      prevCount !== null ? prevCount + amount : amount
    );
  };

  const fetchUserId = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/idFetch`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data && typeof response.data.userId === "string")
        setUserId(response.data.userId);
    } catch (err) {
      logError("GameDataProvider: Error fetching user id", err);
    }
  };

  const addItem = (item: GameItem) => {
    item.quantity++;
    item.price = calculateNewPrice(item.price, item.quantity);
  };

  const buyItem = (itemPrice: number) => {
    // Use (coinCount ?? 0) to default to 0 when coinCount is null
    if ((coinCount ?? 0) >= itemPrice) {
      setCoinCount((coinCount ?? 0) - itemPrice);
      return true; // Purchase successful
    } else {
      return false; // Not enough coins
    }
  };

  useEffect(() => {
    fetchCharacterData();
    fetchItemHeirarchies();
    fetchBuildCatalog();
  }, []);

  const refreshStars = async () => {
    try {
      const token = await getAccessToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/stars`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const starCount = Number(res.data?.starCount ?? 0);
      const lastTs = res.data?.lastStarTimestamp
        ? new Date(res.data.lastStarTimestamp).getTime()
        : Date.now();
      const regenSeconds = Number(res.data?.regenSeconds ?? 240);
      const cap = Number(res.data?.cap ?? 100);

      setStarsCount(starCount);
      setLastStarTimestamp(lastTs);
      setStarMeta({ regenSeconds, cap });

      starsRef.current = starCount;
      lastTsRef.current = lastTs;
      metaRef.current = { regenSeconds, cap };

      setStarCountdown(
        computeCountdown(lastTs, Date.now(), regenSeconds, starCount, cap)
      );
    } catch (err) {
      logError("GameDataProvider: Failed to refresh stars", err);
    }
  };

  const fetchSearchedUsers = async (usernameSearch: string) => {
    try {
      const token = await getAccessToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/search-by-name`,
        {
          usernameSearch: usernameSearch, // This will be added as a body parameter
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.foundUsers) return response.data.foundUsers;
    } catch (err) {
      logError("GameDataProvider: Error fetching user id", err);
      return;
    }
  };

  useEffect(() => {
    if (!accessToken) return;

    const fetchAllResources = async () => {
      try {
        const moonrockRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/moonrocks`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        const starsRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/stars`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        // Then set them:
        if (moonrockRes.data) {
          setCoinCount(moonrockRes.data.coinCount);
          setMoonrockCount(moonrockRes.data.moonrockCount);
        }
        if (starsRes.data) {
          setStarsCount(starsRes.data.starCount);
        }
      } catch (error) {
        logError("GameDataProvider: Error fetching resources", error);
      } finally {
        setDataReady(true);
      }
    };

    fetchAllResources();
  }, [accessToken]);

  const contextValue: GameDataContextType = {
    coinCount: coinCount ?? 0, // Provide fallback values if desired
    dataReady,
    moonrockCount: moonrockCount ?? 0,
    starsCount: starsCount ?? 0,

    addItem,
    buyItem,
    addMoonrock,
    showGrid,
    setShowGrid,
    unlockedCharacters,
    setUnlockedCharacters,

    unlockCharacter,
    fetchCharacterData,

    addCoins, // Added
    setStarsCount,
    setMoonrockCount,
    setCoinCount,

    refreshStars,
    setLastStarTimestamp,
    setStarMeta,

    starMeta,
    lastStarTimestamp,

    unlockedItems,
    fetchUnlockedItems,
    itemHierarchies,
    userId,
    fetchUserId,
    fetchNotifications,
    pendingNotifications,
    setPendingNotifications,
    updateNotifications,
    fetchSearchedUsers,

    buildCategories,
    fetchBuildCatalog,
    starCountdown,
    setStarCountdown,
  };

  return (
    <GameDataContext.Provider value={contextValue}>
      {children}
    </GameDataContext.Provider>
  );
};

export default GameDataProvider;
